import Table from "@shared/components/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { currencyService } from "@modules/payments/currency/services/currency";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import DeleteModal from "@shared/components/DeleteModal";
import CurrencyForm from "./CurrencyForm";

export default function Currency() {

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedCurrencyId, setSelectedCurrencyId] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const [currencies, setCurrencies] = useState([]);

    const { t } = useTranslation();
    const columns = [
        { key: "codeIso", label: t('payments.currencies.form.digitalCode') },
        { key: "mnemonic", label: t('payments.currencies.form.code') },
        { key: "description", label: t('payments.currencies.form.currency') },
    ]

    const formProps = useForm({
        defaultValues: {
            digitalCode: '',
            mnemonic: '',
            description: '',
        }
    });


    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={() => handleOpenModal('add')}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>
            <button
                className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                disabled={!selectedRecord}
                onClick={() => handleOpenModal('edit')}
                title={t('common.edit')}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
                className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                disabled={!selectedRecord}
                onClick={() => handleOpenModal('delete')}
                title={t('common.delete')}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </>
    );

    const fetchCurrencies = async () => {
        const response = await currencyService.getCurrencies();
        setCurrencies(response);
    }

    useEffect(() => {
        fetchCurrencies();
    }, []);

    const handleOpenModal = (type) => {
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setSelectedRecord(null);
        setSelectedCurrencyId(null);
    };

    const handleSelectCurrency = (currency) => {
        if (selectedRecord && selectedRecord.codeIso === currency.codeIso) {
            setSelectedCurrencyId(null);
            setSelectedRecord(null);
        } else {
            setSelectedCurrencyId(currency.codeIso);
            setSelectedRecord(currency);
        }
    };

    const handleCheckboxChange = (index) => {
        const currency = currencies[index];
        handleSelectCurrency(currency);
    };

    const handleConfirm = async (formData) => {
        try {
            if (modalState.type === 'add') {
                const createData = {
                    ...formData,
                    isActive: formData.active,
                    decimals: false,
                    per: 1,
                    oficial: 0
                };
                await currencyService.createCurrency(createData);
                notifySuccess(t('common.createdSuccessfully'));
            } else if (modalState.type === 'edit' && selectedRecord) {
                const updateData = {
                    ...formData,
                    isActive: formData.active,
                    decimals: selectedRecord.decimals,
                    per: selectedRecord.per,
                    oficial: selectedRecord.oficial
                };
                await currencyService.updateCurrency(selectedRecord.codeIso, updateData);
                notifySuccess(t('common.updatedSuccessfully'));
            }
            await fetchCurrencies();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving record:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await currencyService.deleteCurrency(selectedRecord.codeIso);
            notifySuccess(t('common.deletedSuccessfully'));
            await fetchCurrencies();
            handleCloseModal();
        } catch (error) {
            console.error('Error deleting record:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    return (
        <>
            <Table
                columns={columns}
                actionButtons={actionButtons}
                showCustomSearch={true}
                customSearchComponent={null}
                title={t('payments.currencies.title')}
                showPagination={false}
                showPageSizeSelector={false}
                data={currencies}
                selectedData={selectedRecord?.codeIso}
                handleCheckboxChange={handleCheckboxChange}
            />

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <CurrencyForm
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                    formProps={formProps}
                />
            )}

            {modalState.visible && modalState.type === 'delete' && selectedRecord && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleDeleteConfirm}
                    name={selectedRecord.description}
                    entityType="record"
                />
            )}
        </>
    );
}

